import { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { useAnalytics } from 'use-analytics';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import { useSelector } from 'react-redux';
import useScrollToElement from '../../hooks/useScrollToElement';
import Button from '../Button';
import useStyles from './styles';
import MessageBubble from './MessageBubble';
import { askChatGPT, askChatGPTStream } from './api';
import { getTokenCount } from './helpers/tokenTools';
import {
  useNLPConstants,
  useNLPEnums,
  useNLPInstructions,
  TEMP_NUMBER,
  TEMP_BOOLEAN,
  TEMP_STRING,
} from './constants/nlpSettings';
import trimConversationToFitTokenLimit from './helpers/trimConversationToFitTokenLimit';
import generateGeneralIntroMessage from './introMessages/generalIntroMessage';
import ChatbotTextArea from './chatbotTextarea';
import { ASK_AI_LABEL } from '../../constants';
/**
 * Function renders the ChatGPT interface, prepare intro message about a business, and manage
 * the conversation with NLP model.
 * @param {rawData} - the data where the extractor will extract info to create intro message.
 * @param {extractor} - a function that takes in rawData and process it to return the intro message.
 * Please refer to extractors/hotelExtractor for details about how to build an extractor.
 * @param {utilFunctions} - a set of helper functions to be used in the component.
 * @param {disabled} - a boolean to disable the component.
 * @param {generalIntro} - a general intro message to be used before the data are ready.
 * @param {siteType} - the type of the website where the component is used. (e.g., 'HOTEL','CRUISE')
 * @returns an intro message and a function to trigger extractor to start processing rawData. We
 * need the trigger, because we want to start the extractor only at a specific time.
 */
const ChatGPTInterface = ({
  rawData,
  extractor,
  utilFunctions,
  disabled = false,
  generalIntro,
  siteType = 'DEFAULT',
  newDesign,
  instructionName,
}) => {
  // Get config constants
  const nlpConstants = useNLPConstants();
  const MAX_IRRELEVANT_QUERY_COUNT = nlpConstants
    ? parseInt(nlpConstants.MAX_IRRELEVANT_QUERY_COUNT.value, 10)
    : TEMP_NUMBER;
  const MAX_QUERY_TOKEN_COUNT = nlpConstants
    ? parseInt(nlpConstants.MAX_QUERY_TOKEN_COUNT.value, 10)
    : TEMP_NUMBER;
  const MAX_NUM_HISTORICAL_MESSAGES = nlpConstants
    ? parseInt(nlpConstants.MAX_NUM_HISTORICAL_MESSAGES.value, 10)
    : TEMP_NUMBER;
  const SHOULD_STREAM_CHATGPT_RESPONSE = nlpConstants
    ? nlpConstants.SHOULD_STREAM_CHATGPT_RESPONSE.value === 'true'
    : TEMP_BOOLEAN;

  // Get enums constants
  const nlpEnums = useNLPEnums();
  const DATA_SUCCESS = nlpEnums ? nlpEnums.DATA_LOADING_STATUS.SUCCESS : TEMP_STRING;
  const DATA_FAILED = nlpEnums ? nlpEnums.DATA_LOADING_STATUS.FAILED : TEMP_STRING;
  const DATA_IN_PROCESS = nlpEnums ? nlpEnums.DATA_LOADING_STATUS.IN_PROCESS : TEMP_STRING;
  const PLACEHOLDER_TEXT = nlpEnums ? nlpEnums.INTERFACE_PLACEHOLDER_TEXT[siteType] : TEMP_STRING;

  // Get NLP instructions
  const nlpInstructions = useNLPInstructions();

  // If generalIntro is not provided, we generate a default one
  const generalIntroLocal =
    generalIntro || generateGeneralIntroMessage(nlpConstants, nlpInstructions);

  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState([]);
  const [waitingForChatGPT, setWaitingForChatGPT] = useState(false);
  const [irrelevantQueryCount, setIrrelevantQueryCount] = useState(0);
  const [isChatHidden, setIsChatHidden] = useState(false);
  const classes = useStyles();
  const { track } = useAnalytics();

  const [hasRequestedIntro, setHasRequestedIntro] = useState(false);
  const [intro, setIntro] = useState(null);
  const [dataLoadingStatus, setDataLoadingStatus] = useState('');
  const { id: travelAgencyId } = useSelector((store) => store.user.travelAgency);
  // We use triggerExtract to start the logic in the extractor and fill the introMessage
  const {
    introMessage,
    triggerExtract,
    extractorError = null,
    setExtractorError = () => {},
  } = extractor(rawData);

  // Once the introMessage from extractor filled, we set it as the intro
  useEffect(() => {
    if (introMessage && intro !== introMessage) {
      setDataLoadingStatus(DATA_SUCCESS);
      setIntro(introMessage);
    }
  }, [intro, introMessage, DATA_SUCCESS]);

  // If error occurs within extractor, we reset the state to allow user to try again
  useEffect(() => {
    if (extractorError) {
      setExtractorError(null);
      setDataLoadingStatus(DATA_FAILED);
      setHasRequestedIntro(false);
    }
  }, [extractorError, setExtractorError, DATA_FAILED]);

  /* When the user clicks on the text field for the first time, we will
   * immediately request info about the business (hotel, cruise, etc...).
   * Then when the user submits the query, we will have the business ready. */
  const handleClickOnInputField = () => {
    if (!hasRequestedIntro) {
      setDataLoadingStatus(DATA_IN_PROCESS);
      setHasRequestedIntro(true);
      triggerExtract(true);
    }
  };

  const askButtonIsEnabled = inputText.trim() !== '' && !waitingForChatGPT;
  const [needsScrollIntoView, setNeedsScrollIntoView] = useState(false);
  const mostRecentMessageEl = useScrollToElement(
    {
      value: needsScrollIntoView,
      options: {
        behavior: 'smooth',
        block: 'nearest',
      },
    },
    () => {
      setNeedsScrollIntoView(false);
    },
  );

  // Get the necessary util functions or return if they are not available
  const { isQueryRelevant, isQuerySensitive, resetEmailMessage } = utilFunctions;
  if (!isQueryRelevant || !isQuerySensitive) return null;

  /*
    Messages are of the form
    {
      author: 'user' | 'chatgpt' | 'chatgpt-error',
      content: string,
      tag?: 'sensitive',
    }
  */

  const appendMessage = (message) => {
    setMessages((oldMessages) => [
      ...oldMessages,
      {
        ...message,
        id: oldMessages.length,
      },
    ]);
    setNeedsScrollIntoView(true);
  };

  const editLastMessage = ({ newContent, newAuthor }) => {
    setMessages((oldMessages) => {
      // If something went wrong
      if (oldMessages.length === 0) {
        return oldMessages;
      }
      return [
        ...oldMessages.slice(0, -1), // Everything except the last message
        {
          ...oldMessages.at(-1),
          author: newAuthor,
          content: newContent,
        },
      ];
    });
  };

  const sendToChatGPT = async (conversationHistory) => {
    setWaitingForChatGPT(true);

    /* Start by appending an ... animation to the ChatGPT bubble.
     * The animation will continue until receive some update from ChatGPT */
    const messageObject = {
      author: 'chatgpt',
      content: <span className={classes.ellipsis} />,
    };
    if (!intro) {
      /* We want to separate responses before and after the data are ready,
       * because knowledge in prior conversation will affect new responses.
       * [Small logic leak] it's possible data is ready at this moment, but the
       * conversationHistory still holds general intro, but that's fine */
      messageObject.tag = 'before-data-ready';
    }
    appendMessage(messageObject);

    // For tracking the conversationHistory we send, but without the first intro message
    const visibleMessages = conversationHistory.slice(1);
    track(`ChatGPTInterface.sendQuery${siteType ? `.${siteType}` : ''}`, visibleMessages);

    // Try to get response from ChatGPT
    try {
      if (SHOULD_STREAM_CHATGPT_RESPONSE) {
        const fullMessage = await askChatGPTStream({
          messages: conversationHistory,
          nlpConstants,
          onUpdateMessage: (newContent) => {
            // Modify the most recent message
            editLastMessage({ newContent });
            // if (resetEmailMessage) {
            //   resetEmailMessage(newContent);
            // }
          },
          resetEmailMessage,
          instructionName,
          travelAgencyId,
        });
        track('ChatGPTInterface.receiveResponse', {
          response: fullMessage,
          previousMessages: visibleMessages,
          siteType: siteType || 'DEFAULT',
        });
      } else {
        const response = await askChatGPT({
          messages: conversationHistory,
          nlpConstants,
        });
        // Edit the last message
        if (resetEmailMessage) {
          resetEmailMessage(response);
        }
        editLastMessage({ newContent: response });
        track('ChatGPTInterface.receiveResponse', {
          response,
          previousMessages: visibleMessages,
          siteType: siteType || 'DEFAULT',
        });
      }
    } catch (error) {
      editLastMessage({
        newAuthor: 'chatgpt-error',
        newContent: error.message,
      });
      track('ChatGPTInterface.receiveError', {
        error: error.message,
        messages: visibleMessages,
      });
    }

    setWaitingForChatGPT(false);
  };

  const handleSubmitQuery = (event) => {
    event.preventDefault();

    // Handle the cases that we are not ready to ask ChatGPT
    if (inputText === '') return;
    if (!generalIntroLocal && !intro) return;
    if (waitingForChatGPT) return;

    setIsChatHidden(false);

    const query = inputText.trim();
    const message = {
      author: 'user',
      content: query,
    };

    // Check if the query is valid or not
    let invalidQueryMessage;
    if (isQuerySensitive(query)) {
      invalidQueryMessage =
        'Error: your query appears to contain sensitive information, so it will not be sent to AI.';
      message.tag = 'sensitive';
    } else if (getTokenCount(query) >= MAX_QUERY_TOKEN_COUNT) {
      invalidQueryMessage = 'This message is too long. Please split it into smaller parts.';
      message.tag = 'invalid';
    } else if (!isQueryRelevant(query)) {
      if (irrelevantQueryCount >= MAX_IRRELEVANT_QUERY_COUNT) {
        invalidQueryMessage = 'Error: your query appears not to be relevant.';
        message.tag = 'invalid';
      }
      setIrrelevantQueryCount((oldValue) => oldValue + 1);
    }

    // Display the message in the UI
    appendMessage(message);
    const newMessages = [...messages, message];
    setInputText('');

    if (invalidQueryMessage) {
      // Warn user that their query is irrelevant
      appendMessage({
        author: 'chatgpt-error',
        content: invalidQueryMessage,
      });
    } else {
      // Send message to ChatGPT and wait for response

      let trimmedMessages = newMessages.filter(
        (m) => m.tag !== 'sensitive' && m.tag !== 'invalid' && m.author !== 'chatgpt-error',
      );
      if (trimmedMessages.length > MAX_NUM_HISTORICAL_MESSAGES) {
        trimmedMessages = trimmedMessages.slice(-MAX_NUM_HISTORICAL_MESSAGES);
      }

      const conversationHistory = [
        {
          role: 'system',
          content: intro || generalIntroLocal,
        },
        ...trimmedMessages.map((m) => ({
          role: m.author === 'user' ? 'user' : 'assistant',
          content: m.content,
        })),
      ];
      const trimmedConversation = trimConversationToFitTokenLimit(
        conversationHistory,
        nlpConstants,
        rawData.emailMessage || newDesign,
      );
      sendToChatGPT(trimmedConversation);
    }
  };
  if (disabled) return null;
  const DataStatus = () => (
    <span>
      {dataLoadingStatus === DATA_IN_PROCESS ? (
        <>
          <span className={classes.dataLoadingText}>{dataLoadingStatus}</span>
          <span className={classes.dataLoadingDots}>
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </>
      ) : (
        <span
          className={
            dataLoadingStatus === DATA_SUCCESS
              ? classes.dataLoadedText
              : classes.dataLoadingFailedText
          }
        >
          {dataLoadingStatus}
          {dataLoadingStatus === DATA_FAILED && (
            <ClearIcon style={{ color: '#FF4646', marginLeft: '0.3em' }} />
          )}
          {dataLoadingStatus === DATA_SUCCESS && (
            <CheckIcon style={{ color: '#39ac69', marginLeft: '0.3em' }} />
          )}
        </span>
      )}
    </span>
  );

  const AskButton = () => (
    <Button
      className="ml-2"
      variant={askButtonIsEnabled ? Button.buttonVariants.PRIMARY : Button.buttonVariants.SECONDARY}
      disabled={!askButtonIsEnabled}
      size={Button.buttonSizes.SM}
      onClick={handleSubmitQuery}
    >
      Ask
    </Button>
  );
  const AIWarningText = ({ className }) => (
    <div className={className}>AI can make mistakes. Please check important info.</div>
  );
  AIWarningText.propTypes = {
    className: PropTypes.string,
  };
  AIWarningText.defaultProps = {
    className: '',
  };
  return (
    <>
      <div
        className={`${newDesign ? 'w-ful px-4 my-2 pt-1 pb-2 border-gray-300 bg-white shadow' : 'bg-body-0 px-4 py-4 rounded-md'} rounded-md`}
      >
        {!newDesign ? (
          <>
            <div className="font-bold flex items-center justify-between">
              <span>{ASK_AI_LABEL}</span>
              <DataStatus />
            </div>
            <form className="mt-4" onSubmit={handleSubmitQuery} disabled={!askButtonIsEnabled}>
              <div className="flex items-justify">
                <ChatbotTextArea
                  inputText={inputText}
                  setInputText={setInputText}
                  handleClickOnInputField={handleClickOnInputField}
                  PLACEHOLDER_TEXT={PLACEHOLDER_TEXT}
                  handleSubmitQuery={handleSubmitQuery}
                />
                <AskButton />
              </div>
            </form>
          </>
        ) : (
          <div className="flex flex-col md:flex-row items-start gap-5 w-full py-1">
            <div className="font-bold flex items-center w-full md:w-auto mb-2 sm:mb-0">
              <span>
                {ASK_AI_LABEL}
                <AIWarningText className="font-normal text-xs" />
              </span>
            </div>
            <form
              className="flex-1 flex w-full"
              onSubmit={handleSubmitQuery}
              disabled={!askButtonIsEnabled}
            >
              <ChatbotTextArea
                inputText={inputText}
                setInputText={setInputText}
                handleClickOnInputField={handleClickOnInputField}
                PLACEHOLDER_TEXT={PLACEHOLDER_TEXT}
                handleSubmitQuery={handleSubmitQuery}
              />
              <AskButton />
            </form>
          </div>
        )}
        <div className={isChatHidden ? classes.hiddenChat : classes.chat}>
          {messages.map((message, index) => (
            <div key={message.id} ref={index === messages.length - 1 ? mostRecentMessageEl : null}>
              <MessageBubble
                message={message}
                previousMessageWasBySameAuthor={
                  index > 0 && messages[index - 1].author === message.author
                }
                key={message.id}
              />
            </div>
          ))}
        </div>
        {messages.length > 0 && (
          <div className="mt-4 flex flex-col place-items-end">
            <button
              onClick={() => setIsChatHidden(!isChatHidden)}
              type="button"
              className="text-theme text-sm mt-1 focus:outline-none email-hidden-field"
            >
              {isChatHidden && (
                <>
                  <span>View chat </span>
                  <FaChevronDown className="inline-block" />
                </>
              )}
              {!isChatHidden && (
                <>
                  <span>Hide chat </span>
                  <FaChevronUp className="inline-block" />
                </>
              )}
            </button>
          </div>
        )}
        {!newDesign && <AIWarningText className="text-xs text-center mt-2 sm:mt-0" />}
      </div>
    </>
  );
};

ChatGPTInterface.propTypes = {
  rawData: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  extractor: PropTypes.func.isRequired,
  utilFunctions: PropTypes.shape({
    isQueryRelevant: PropTypes.func.isRequired,
    isQuerySensitive: PropTypes.func.isRequired,
    resetEmailMessage: PropTypes.func,
  }).isRequired,
  disabled: PropTypes.bool,
  generalIntro: PropTypes.string,
  siteType: PropTypes.string,
  newDesign: PropTypes.bool,
  instructionName: PropTypes.string,
};

ChatGPTInterface.defaultProps = {
  disabled: false,
  generalIntro: undefined,
  siteType: 'DEFAULT',
  newDesign: false,
  instructionName: '',
};

export default ChatGPTInterface;
